/**
 * Login Page
 * Handles user authentication
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
} from '@mui/icons-material';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  signIn,
  completeNewPassword,
  forgotPasswordRequest,
  forgotPasswordSubmitAction,
  setForgotPassword,
  clearError,
} from '../../store/slices/authSlice';
import { colorPalette } from '../../theme';

// Dark background matching original AsseTrac login
const loginBackground = colorPalette.backgroundDarker;

const LoginPage = () => {
  const dispatch = useAppDispatch();
  const {
    loading,
    error,
    passwordChangeRequired,
    forgotPassword,
    forgotPasswordSubmit,
    pendingUser,
  } = useAppSelector((state) => state.auth);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    dispatch(clearError());

    if (!username || !password) {
      setLocalError('Please enter username and password');
      return;
    }

    dispatch(signIn({ username, password }));
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    dispatch(completeNewPassword({ user: pendingUser, newPassword }));
  };

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!username) {
      setLocalError('Please enter your email address');
      return;
    }

    dispatch(forgotPasswordRequest({ username }));
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!verificationCode || !newPassword) {
      setLocalError('Please enter verification code and new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    dispatch(forgotPasswordSubmitAction({ 
      username, 
      code: verificationCode, 
      newPassword 
    }));
  };

  const displayError = error || localError;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: loginBackground,
        padding: 2,
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: `linear-gradient(to bottom, rgba(0,0,0,0.3), transparent)`,
          pointerEvents: 'none',
        }}
      />

      <Card
        elevation={16}
        sx={{
          maxWidth: 400,
          width: '100%',
          position: 'relative',
          zIndex: 1,
          borderRadius: '16px',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo/Brand - matching MATRAC by ASSETRAC */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: colorPalette.asseTracOrange,
                  letterSpacing: '3px',
                }}
              >
                MATRAC
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: '1px', fontSize: '0.75rem' }}
              >
                by ASSETRAC
              </Typography>
            </Box>
          </Box>

          {/* Error Alert */}
          {displayError && (
            <Alert severity="error" sx={{ mb: 3, backgroundColor: '#3D1A18', border: '1px solid #EC4F36', color: '#E8DCDA' }}>
              {displayError}
            </Alert>
          )}

          {/* Login Form */}
          {!passwordChangeRequired && !forgotPassword && !forgotPasswordSubmit && (
            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                name="email"
                label="Email"
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="email"
                autoFocus
              />
              <TextField
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 3 }}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                sx={{ mb: 2, borderRadius: '9999px', height: 44 }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => dispatch(setForgotPassword(true))}
                  sx={{ color: 'text.secondary' }}
                >
                  Forgot password?
                </Link>
              </Box>
            </Box>
          )}

          {/* New Password Form */}
          {passwordChangeRequired && (
            <Box component="form" onSubmit={handleNewPassword}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Please set a new password to continue.
              </Typography>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 3 }}
                autoComplete="new-password"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Set New Password'}
              </Button>
            </Box>
          )}

          {/* Forgot Password Form */}
          {forgotPassword && (
            <Box component="form" onSubmit={handleForgotPasswordRequest}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Enter your email address to receive a password reset code.
              </Typography>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{ mb: 3 }}
                autoComplete="email"
                autoFocus
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => dispatch(setForgotPassword(false))}
                  sx={{ color: 'text.secondary' }}
                >
                  Back to login
                </Link>
              </Box>
            </Box>
          )}

          {/* Reset Password with Code Form */}
          {forgotPasswordSubmit && (
            <Box component="form" onSubmit={handleForgotPasswordSubmit}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Enter the verification code sent to your email.
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                sx={{ mb: 2 }}
                autoFocus
              />
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                autoComplete="new-password"
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 3 }}
                autoComplete="new-password"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      {/* Footer removed per Mark feedback */}
    </Box>
  );
};

export default LoginPage;
